'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Filters } from './components/Filters';
import { SalesSummary } from './components/SalesSummary';
import { WeekdayAnalysis } from './components/WeekdayAnalysis';
import { PriceQuadrants } from './components/PriceQuadrants';
import { TicketEffortAnalysis } from './components/TicketEffortAnalysis';

interface SalesData {
  totalAmount: number;
  quantity: number;
  average: number;
  standardDeviation: number;
}

interface WeekdayData {
  day: string;
  quantity: number;
  total: number;
  average: number;
}

interface PriceQuadrantsData {
  quadrants: {
    low_price_low_qty: any;
    low_price_high_qty: any;
    high_price_low_qty: any;
    high_price_high_qty: any;
  };
  summary: {
    total_orders: number;
    total_value: number;
    price_median: number;
  };
}

interface TicketMetrics {
  totalTickets: number;
  totalTicketTimeHours: string;
  averageTicketTimeMinutes: string;
  averageWaitingTimeMinutes: string;
  averageMessagingTimeMinutes: string;
  averageTransferCount: number;
}

export default function Home() {
  const [salesData, setSalesData] = useState<SalesData | null>(null);
  const [weekdayData, setWeekdayData] = useState<WeekdayData[]>([]);
  const [priceQuadrantsData, setPriceQuadrantsData] = useState<PriceQuadrantsData | null>(null);
  const [ticketMetrics, setTicketMetrics] = useState<TicketMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    vendedor: 'all',
  });

  const fetchData = async (newFilters: { startDate: string; endDate: string; vendedor: string }) => {
    setLoading(true);
    setFilters(newFilters);

    try {
      const params = new URLSearchParams({
        startDate: newFilters.startDate,
        endDate: newFilters.endDate,
        vendedor: newFilters.vendedor,
      });

      const [summary, weekday, quadrants, tickets] = await Promise.all([
        fetch(`/api/analytics/summary?${params}`).then(r => r.json()),
        fetch(`/api/analytics/weekday?${params}`).then(r => r.json()),
        fetch(`/api/analytics/price-quadrants?${params}`).then(r => r.json()),
        fetch(`/api/analytics/tickets-effort?${params}`).then(r => r.json()),
      ]);

      setSalesData(summary);
      setWeekdayData(weekday);
      setPriceQuadrantsData(quadrants);
      setTicketMetrics(tickets);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshData = async () => {
    setSyncing(true);
    setSyncMessage('Sincronizando dados...');
    
    try {
      // First migrate if needed
      try {
        await fetch('/api/admin/migrate', { method: 'POST' });
      } catch (e) {
        console.log('Migration endpoint not available or already migrated');
      }

      // Sync users
      const usersResponse = await fetch('/api/admin/sync-users', { method: 'POST' });
      const usersData = await usersResponse.json();
      console.log('Users synced:', usersData);

      // Sync tickets
      const ticketsResponse = await fetch('/api/admin/sync-tickets', { method: 'POST' });
      const ticketsData = await ticketsResponse.json();
      console.log('Tickets synced:', ticketsData);

      setSyncMessage('✓ Dados atualizados com sucesso');
      
      // Refresh the displayed data
      if (filters.startDate && filters.endDate) {
        await fetchData(filters);
      }

      // Clear message after 3 seconds
      setTimeout(() => setSyncMessage(''), 3000);
    } catch (error) {
      console.error('Error syncing data:', error);
      setSyncMessage('✗ Erro ao sincronizar dados');
      setTimeout(() => setSyncMessage(''), 3000);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-6xl">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-5xl font-bold mb-3">NZB Embalagens</h1>
          <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-4"></div>
          <p className="text-slate-400 text-lg">Performance de Vendas</p>
          
          {/* Navigation Links */}
          <div className="flex justify-center gap-4 mt-6" style={{
            margin: '1em'
          }}>
            <button
              className="px-6 py-2 rounded-lg font-medium transition-all border"
              style={{
                backgroundColor: 'var(--accent)',
                color: '#fff',
                borderColor: 'var(--accent)',
                padding: '0.5em 1em'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent)';
              }}
              disabled
            >
              📊 Dashboard
            </button>
            <Link
              href="/comparison"
              className="px-6 py-2 rounded-lg font-medium transition-all border"
              style={{
                backgroundColor: 'rgba(148, 163, 184, 0.2)',
                color: '#e2e8f0',
                borderColor: 'var(--card-border-bright)',
                padding: '0.5em 1em'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.2)';
              }}
            >
              🔄 Comparação
            </Link>
          </div>
        </div>

        {/* Sync Status Message */}
        {syncMessage && (
          <div className="mb-6 p-4 rounded-lg bg-slate-900/50 backdrop-blur border border-slate-700/50 text-center">
            <p className="text-slate-300">{syncMessage}</p>
          </div>
        )}

        {/* Filters and Refresh Button */}
        <div className="flex flex-col gap-4 mb-8">
          <Filters onFilterChange={fetchData} />
          <button
            style={{ marginBottom: '1em', padding: '1em' }}
            onClick={handleRefreshData}
            disabled={syncing || loading}
            className="px-6 py-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-slate-600 disabled:to-slate-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            {syncing ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-slate-300 border-t-white rounded-full animate-spin"></span>
                Sincronizando...
              </>
            ) : (
              <>
                <span>🔄</span>
                Atualizar Dados do Digisac
              </>
            )}
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block">
              <div className="w-12 h-12 border-4 border-slate-400 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-400 mt-6 text-lg">Carregando dados...</p>
            </div>
          </div>
        )}

        {/* Content */}
        {!loading && salesData ? (
          <>
            {/* Sales Summary */}
            <SalesSummary
              totalAmount={salesData?.totalAmount}
              quantity={salesData?.quantity}
              average={salesData?.average}
              standardDeviation={salesData?.standardDeviation}
            />

            {/* Ticket Effort Analysis */}
            <div className="mb-8" style={{ marginTop: '1em' }}>
              <TicketEffortAnalysis metrics={ticketMetrics} loading={loading} />
            </div>

            {/* Weekday Analysis */}
            {weekdayData && weekdayData.length > 0 ? (
              <WeekdayAnalysis data={weekdayData} />
            ) : (
              <div className="rounded-lg border p-6 mb-8 text-center" style={{
                backgroundColor: 'var(--card-bg)',
                borderColor: 'var(--card-border-bright)',
              }}>
                <p className="text-slate-400">Nenhum dado disponível para os filtros selecionados</p>
              </div>
            )}

            {/* Price Quadrants */}
            {priceQuadrantsData ? (
              <PriceQuadrants data={priceQuadrantsData} />
            ) : (
              <div className="rounded-lg border p-6 mb-8 text-center" style={{
                backgroundColor: 'var(--card-bg)',
                borderColor: 'var(--card-border-bright)',
              }}>
                <p className="text-slate-400">Nenhum dado dos quadrantes disponível para os filtros selecionados</p>
              </div>
            )}
          </>
        ) : !loading ? (
          <div className="rounded-lg border p-12 text-center" style={{
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border-bright)',
          }}>
            <p className="text-slate-400 text-lg">Selecione filtros e aplique para visualizar dados</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
